import { useEffect, useState } from "react";
import { api, fmtAUD } from "@/lib/api";
import { motion } from "framer-motion";
import { Check, ShieldCheck, ArrowUpRight } from "lucide-react";
import PreviewBanner from "./PreviewBanner";

// Bento grid product page — 12-cell layout, dense, techy
export default function ProductBento() {
  const [p, setP] = useState(null);
  useEffect(() => {
    api.get("/products/pixel-9-pro-xl").then((r) => setP(r.data));
  }, []);
  if (!p) return <div className="pt-40 px-6">Loading…</div>;

  return (
    <div className="pt-24 pb-24 px-6 md:px-10" data-testid="preview-product-bento">
      <PreviewBanner title="Bento Specs" kind="Product page" />

      <div className="overline mb-4">Preview / Product · Bento</div>
      <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9]">
        {p.name.split("//")[0]}<span className="text-[#7C3AED]">//</span>Phantom
      </h1>

      <div className="mt-8 grid grid-cols-12 grid-rows-[auto_auto_auto] gap-3 md:gap-4 auto-rows-min">
        {/* Hero image — 7 cols x 2 rows */}
        <motion.div
          initial={{ opacity: 0, clipPath: "inset(100% 0 0 0)" }}
          animate={{ opacity: 1, clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
          className="col-span-12 md:col-span-7 row-span-2 relative overflow-hidden border border-[#1A1A1A] min-h-[420px] md:min-h-[640px] bg-[#080808]"
        >
          <img src={p.image_url} alt="" className="absolute inset-0 w-full h-full object-cover img-clip phantom" />
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="tier-dot phantom" />
            <span className="overline text-[#7C3AED]">TIER 03 · PHANTOM</span>
          </div>
          <div className="absolute bottom-4 left-4 right-4 flex justify-between overline text-[#666]">
            <span>SV · {p.slug.toUpperCase()}</span>
            <span>SN · ██████</span>
          </div>
        </motion.div>

        {/* Price cell */}
        <div className="col-span-6 md:col-span-5 border border-[#1A1A1A] p-6 md:p-8 flex flex-col justify-between min-h-[300px]">
          <div className="overline">Price · AUD</div>
          <div className="font-display text-5xl md:text-7xl">{fmtAUD(p.price_aud)}</div>
          <div className="flex gap-2">
            <button className="btn-vault danger flex-1 justify-center">Buy now</button>
            <button className="btn-vault flex-1 justify-center">+ Cart</button>
          </div>
        </div>

        {/* Tagline cell */}
        <div className="col-span-6 md:col-span-5 border border-[#1A1A1A] p-6 md:p-8 min-h-[300px] flex flex-col justify-between bg-[#080808]">
          <div className="overline">Doctrine</div>
          <p className="font-display text-2xl md:text-3xl leading-tight">{p.tagline}</p>
          <div className="flex items-center gap-2 text-[#444] text-xs overline"><ShieldCheck size={14} /> Hand hardened · Sydney</div>
        </div>

        {/* Spec tiles — 4 mini cells */}
        {Object.entries(p.specs).slice(0, 4).map(([k, v]) => (
          <div key={k} className="col-span-6 md:col-span-3 border border-[#1A1A1A] p-5 min-h-[130px]">
            <div className="overline text-[#444]">{k}</div>
            <div className="text-[#EAEAEA] font-mono text-base mt-3">{v}</div>
          </div>
        ))}

        {/* Features tile — 8 cols */}
        <div className="col-span-12 md:col-span-8 border border-[#1A1A1A] p-6 md:p-8">
          <div className="overline mb-4">Included</div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
            {p.features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-[#EAEAEA] text-sm">
                <Check size={16} className="mt-0.5 text-[#7C3AED]" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA compare */}
        <div className="col-span-12 md:col-span-4 border border-[#1A1A1A] p-6 md:p-8 flex flex-col justify-between min-h-[220px] bg-[#080808]">
          <div>
            <div className="overline">Not sure?</div>
            <div className="font-display text-2xl mt-2">Compare tiers side-by-side.</div>
          </div>
          <button className="btn-vault w-full justify-center">Open matrix <ArrowUpRight size={14} /></button>
        </div>

        {/* Long description tile — full width */}
        <div className="col-span-12 border border-[#1A1A1A] p-6 md:p-10">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-3">
              <div className="overline">Story</div>
              <div className="font-display text-2xl mt-3">Why we made this.</div>
            </div>
            <div className="col-span-12 md:col-span-9 text-[#888] leading-relaxed text-sm md:text-base">
              {p.long_description}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
